/**
 * Brown Butters — product buy box.
 *
 * The section renders a fully working, no-JS product page: the fragrance swatches are real
 * links (`?variant=…` for a multi-variant product, the sibling product's URL when each
 * fragrance is its own product), and the add-to-cart form is a plain Shopify product form.
 *
 * This module is enhancement only:
 *   1. Same-product variant swatches switch in place instead of reloading — form input,
 *      price, compare-at price, discount badge and availability all repaint.
 *   2. It announces the choice as `shopify:product:select` so the sticky bar and the
 *      gallery follow along.
 *   3. Bundle discount codes copy to the clipboard, and the share button uses the Web
 *      Share API where it exists.
 */

const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const COPIED_MS = 1800;

class MbbProductBuybox extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Array<Record<string, any>> | null} */
  #variants = null;

  connectedCallback() {
    const { signal } = this.#abortController;

    for (const swatch of this.querySelectorAll('[data-mbb-swatch]')) {
      swatch.addEventListener('click', this.#handleSwatchClick, { signal });
    }

    for (const button of this.querySelectorAll('[data-mbb-copy]')) {
      button.addEventListener('click', this.#handleCopy, { signal });
    }

    this.querySelector('[data-mbb-share]')?.addEventListener('click', this.#handleShare, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /** Variant data rendered by the section. Empty for a single-variant product. */
  get variants() {
    if (this.#variants) return this.#variants;

    try {
      const parsed = JSON.parse(this.dataset.variants || '[]');
      this.#variants = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.#variants = [];
    }

    return this.#variants;
  }

  /** @param {MouseEvent} event */
  #handleSwatchClick = (event) => {
    const swatch = /** @type {HTMLElement | null} */ (event.currentTarget);
    const variantId = swatch?.getAttribute('data-variant-id');

    // Swatches that point at a different product are ordinary links — let them navigate.
    if (!swatch || !variantId) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const variant = this.variants.find((entry) => String(entry.id) === String(variantId));
    if (!variant) return;

    event.preventDefault();

    for (const other of this.querySelectorAll('[data-mbb-swatch]')) {
      const active = other === swatch;
      other.classList.toggle('is-active', active);
      if (active) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    }

    this.#applyVariant(variant);

    const url = swatch.getAttribute('href');
    if (url) window.history.replaceState({}, '', url);

    // Non-bubbling, dispatched on the document: the sticky bar and the gallery listen there.
    // Keeping it off the section tree avoids waking Horizon's own variant-change machinery.
    document.dispatchEvent(
      new CustomEvent(PRODUCT_SELECT_EVENT, {
        detail: { variantId: variant.id, mediaId: variant.mediaId, productId: this.dataset.productId },
      })
    );
  };

  /** @param {Record<string, any>} variant */
  #applyVariant(variant) {
    for (const input of this.querySelectorAll('input[data-mbb-variant-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.value = String(variant.id);
      input.disabled = variant.available === false;
    }

    for (const button of this.querySelectorAll('form button[type="submit"]')) {
      if (button instanceof HTMLButtonElement) button.disabled = variant.available === false;
    }

    const price = this.querySelector('[data-mbb-price]');
    if (price && variant.price) price.textContent = variant.price;

    const compare = this.querySelector('[data-mbb-compare]');
    if (compare) {
      compare.textContent = variant.compareAtPrice ?? '';
      compare.toggleAttribute('hidden', !variant.compareAtPrice);
    }

    const badge = this.querySelector('[data-mbb-badge]');
    if (badge instanceof HTMLElement) {
      const template = badge.dataset.mbbBadgeTemplate ?? '';
      const hasDiscount = Number(variant.discount) > 0;
      if (hasDiscount) badge.textContent = template.replace('[percent]', String(variant.discount));
      badge.toggleAttribute('hidden', !hasDiscount);
    }

    const quantity = this.querySelector('[data-mbb-net-quantity]');
    if (quantity instanceof HTMLElement && variant.quantityLabel) {
      quantity.textContent = variant.quantityLabel;
    }
  }

  /** @param {MouseEvent} event */
  #handleCopy = async (event) => {
    const button = /** @type {HTMLElement | null} */ (event.currentTarget);
    const code = button?.getAttribute('data-mbb-copy');
    if (!button || !code) return;

    event.preventDefault();

    const copied = await writeToClipboard(code);
    if (!copied) return;

    const label = button.querySelector('[data-mbb-copy-label]') ?? button;
    const original = label.textContent;
    button.classList.add('is-copied');
    label.textContent = button.getAttribute('data-mbb-copied-label') || 'Copied!';

    window.setTimeout(() => {
      button.classList.remove('is-copied');
      label.textContent = original;
    }, COPIED_MS);
  };

  /** @param {MouseEvent} event */
  #handleShare = async (event) => {
    event.preventDefault();

    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const url = button.getAttribute('data-mbb-share') || window.location.href;
    const title = this.dataset.productTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // A cancelled share is not an error worth reporting.
        if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      }
    }

    const copied = await writeToClipboard(url);
    if (copied) {
      button.classList.add('is-copied');
      window.setTimeout(() => button.classList.remove('is-copied'), COPIED_MS);
    }
  };
}

/**
 * Clipboard write with a document.execCommand fallback for non-secure contexts.
 * @param {string} value
 * @returns {Promise<boolean>}
 */
async function writeToClipboard(value) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to the legacy path.
    }
  }

  const field = document.createElement('textarea');
  field.value = value;
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();

  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }

  field.remove();
  return ok;
}

if (!customElements.get('mbb-product-buybox')) {
  customElements.define('mbb-product-buybox', MbbProductBuybox);
}
